// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from '@/app/user-context';
import { GamesToursView } from '@/features/statistics/GamesToursView';
import { ScoringView } from '@/features/statistics/ScoringView';
import { usePlayerStatistics } from '@/features/statistics/usePlayerStatistics';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';

/** null = all time. */
type Period = number | null;

const PAGES = ['games', 'scoring'] as const;
type PageKey = (typeof PAGES)[number];

/**
 * Personal statistics, as two pages the player moves between: games/rounds/tours first, then
 * scoring. The pages live in one horizontal pager so they can be swiped on a phone, and the
 * segmented control drives the same scroll for pointer users, who have nothing to swipe with.
 */
export default function StatisticsScreen() {
  const { user } = useUser();
  const { t } = useI18n();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { width } = useWindowDimensions();
  const pagerRef = useRef<ScrollView>(null);
  const [page, setPage] = useState<PageKey>('games');
  const [period, setPeriod] = useState<Period>(null);
  const [periodInitialized, setPeriodInitialized] = useState(false);

  const { statistics, gameDefinitions, loading, error, reload } = usePlayerStatistics({
    apiBaseUrl,
    token: user?.token,
    year: period,
  });

  // The first payload reports which years the player has rounds in; land them on the newest
  // one rather than on an all-time view that buries this season.
  useEffect(() => {
    if (periodInitialized || !statistics) {
      return;
    }
    setPeriodInitialized(true);
    const newest = statistics.availableYears[0];
    if (newest !== undefined) {
      setPeriod(newest);
    }
  }, [periodInitialized, statistics]);

  const goToPage = useCallback(
    (next: PageKey) => {
      setPage(next);
      pagerRef.current?.scrollTo({
        x: PAGES.indexOf(next) * width,
        // `pagingEnabled` becomes CSS scroll-snap on web, and a snapping container ignores a
        // smooth programmatic scroll — the tab would highlight while the page stayed put. Snap
        // makes the jump look deliberate anyway; native keeps the animation.
        animated: Platform.OS !== 'web',
      });
    },
    [width]
  );

  const onPagerScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (width <= 0) return;
      const index = Math.round(event.nativeEvent.contentOffset.x / width);
      const next = PAGES[Math.max(0, Math.min(PAGES.length - 1, index))];
      setPage((current) => (current === next ? current : next));
    },
    [width]
  );

  const periods: Period[] = useMemo(() => {
    const years = statistics?.availableYears ?? [];
    return [...years.slice(0, 3), null];
  }, [statistics?.availableYears]);

  if (!user?.token) {
    return (
      <View style={styles.centered}>
        <Text style={styles.message}>{t('statistics.signedOut')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <View style={styles.segment} accessibilityRole="tablist">
        {PAGES.map((key) => (
          <Pressable
            key={key}
            testID={`statistics-tab-${key}`}
            style={[styles.segmentItem, page === key && styles.segmentItemActive]}
            accessibilityRole="tab"
            aria-selected={page === key}
            onPress={() => goToPage(key)}
          >
            <Text style={[styles.segmentText, page === key && styles.segmentTextActive]}>
              {key === 'games' ? t('statistics.tabGames') : t('statistics.tabScoring')}
            </Text>
          </Pressable>
        ))}
      </View>

      {periods.length > 1 ? (
        <View style={styles.chips}>
          {periods.map((value) => (
            <Pressable
              key={value ?? 'all'}
              testID={`statistics-period-${value ?? 'all'}`}
              style={[styles.chip, period === value && styles.chipActive]}
              accessibilityRole="button"
              aria-selected={period === value}
              onPress={() => setPeriod(value)}
            >
              <Text style={[styles.chipText, period === value && styles.chipTextActive]}>
                {value === null ? t('statistics.allTime') : String(value)}
              </Text>
            </Pressable>
          ))}
        </View>
      ) : null}

      {error ? (
        <View style={styles.centered}>
          <Text style={styles.message}>{t(`statistics.${error}`)}</Text>
          <GoffyButton label={t('statistics.retry')} onPress={reload} testID="statistics-retry" />
        </View>
      ) : loading && !statistics ? (
        <View style={styles.centered}>
          <ActivityIndicator size="small" color={appColors.accent.primary} />
          <Text style={styles.message}>{t('statistics.loading')}</Text>
        </View>
      ) : !statistics || statistics.overview.roundsPlayed === 0 ? (
        <View style={styles.centered}>
          <Text style={styles.message}>{t('statistics.emptyTitle')}</Text>
          <Text style={styles.messageHint}>{t('statistics.emptyHint')}</Text>
        </View>
      ) : (
        <ScrollView
          ref={pagerRef}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={32}
          onMomentumScrollEnd={onPagerScroll}
          onScrollEndDrag={onPagerScroll}
          style={styles.pager}
        >
          <ScrollView
            style={[styles.page, { width }]}
            contentContainerStyle={styles.pageContent}
            testID="statistics-page-games"
          >
            <GamesToursView statistics={statistics} gameDefinitions={gameDefinitions} />
          </ScrollView>
          <ScrollView
            style={[styles.page, { width }]}
            contentContainerStyle={styles.pageContent}
            testID="statistics-page-scoring"
          >
            <ScoringView statistics={statistics} />
          </ScrollView>
        </ScrollView>
      )}

      <View style={styles.dots}>
        {PAGES.map((key) => (
          <View key={key} style={[styles.dot, page === key && styles.dotActive]} />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 24,
  },
  message: {
    fontSize: 14,
    color: appColors.text.secondary,
    textAlign: 'center',
  },
  messageHint: {
    fontSize: 12.5,
    color: appColors.text.muted,
    textAlign: 'center',
    maxWidth: 320,
  },
  segment: {
    flexDirection: 'row',
    gap: 4,
    margin: 12,
    marginBottom: 8,
    padding: 3,
    borderRadius: 12,
    backgroundColor: appColors.background.panelAlt,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  segmentItem: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 7,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  segmentItemActive: {
    backgroundColor: appColors.background.elevated,
    borderColor: appColors.border.strong,
  },
  segmentText: {
    fontSize: 12.5,
    fontWeight: '700',
    color: appColors.text.muted,
  },
  segmentTextActive: {
    color: appColors.text.heading,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingHorizontal: 12,
    paddingBottom: 10,
  },
  chip: {
    paddingHorizontal: 13,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
  },
  chipActive: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  chipText: {
    fontSize: 12,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  chipTextActive: {
    color: appColors.text.inverse,
  },
  pager: {
    flex: 1,
  },
  page: {
    flex: 1,
  },
  pageContent: {
    paddingHorizontal: 12,
    paddingBottom: 24,
  },
  dots: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: appColors.border.strong,
  },
  dotActive: {
    backgroundColor: appColors.accent.primary,
  },
});
